import {
  ConfigProvider,
  Modal,
  Row,
  Form,
  Input,
  Flex,
  Switch,
  Typography,
  InputNumber,
  Button,
  message,
  FormInstance
} from "antd";
import cancelIcon from "assets/cancel.png";
import classes from "./styles.module.css";
import { useRef, useState } from "react";
import { createBundle, updateBundle } from "api/bundles";
import { AMOUNT_PER_CREDIT } from "constants/common";
import { COLOR_PRIMARY_ORANGE } from "theme/colors";
import { VALIDATION } from "constants/strings";

type Bundle = {
  id: string;
  description: string;
  credit: number;
  amount: number;
  removed?: boolean;
  active?: boolean;
  updated?: boolean;
};

type Props = {
  showModal: boolean;
  onCancel: () => void;
  bundle: Bundle;
  fetchBundles: () => Promise<void>;
  isDuplicateBundle: (id: string, credit: number, amount: number) => boolean;
};

export const ConfigureBundleModal = ({
  showModal,
  onCancel,
  bundle,
  fetchBundles,
  isDuplicateBundle
}: Props) => {
  const [selectedBundle, setSelectedBundle] = useState<Bundle>(bundle);
  const [loading, setLoading] = useState(false);
  const [isCustomBundle, setIsCustomBundle] = useState(
    AMOUNT_PER_CREDIT.mul(selectedBundle.credit).toNumber() !== selectedBundle.amount
  );
  const creatingBundle = selectedBundle.id.startsWith("temp__");
  const formRef = useRef<FormInstance>(null);

  const saveBundle = async () => {
    const res = await createBundle({
      description: selectedBundle.description.trim(),
      credit: selectedBundle.credit,
      amount: selectedBundle.amount
    });

    if (res.success) {
      message.success("Bundle created successfully");
    }
  };

  const editBundle = async () => {
    const res = await updateBundle(selectedBundle.id, {
      description: selectedBundle.description.trim(),
      credit: selectedBundle.credit,
      amount: selectedBundle.amount,
      active: selectedBundle.active
    });

    if (res.success) {
      message.success("Bundle updated successfully");
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgBase: "#202440"
        },
        components: {
          Modal: {
            contentBg: "#202440",
            colorText: "#fff"
          }
        }
      }}>
      <Modal
        open={showModal}
        footer={null}
        onCancel={onCancel}
        centered
        destroyOnClose
        closeIcon={<img src={cancelIcon} alt="cancel-icon" style={{ padding: "5px" }} />}>
        <Row justify="space-between">
          <h1 className={classes.ModalTitle}>{creatingBundle ? "Create Bundle" : "Edit Bundle"}</h1>
        </Row>

        <Form ref={formRef} layout="vertical" name="bundle-form">
          <Form.Item 
            label="Bundle name"
            name={"name"} 
            className={classes.inputBox}
            initialValue={selectedBundle.description}
            rules={[
              {
                validator: (_, value) => {
                  if(value.trim().length === 0) {
                    return Promise.reject(VALIDATION.fieldIsRequired);
                  }

                  if(value.trim().length < 4) {
                    return Promise.reject(VALIDATION.getMinCharacterValidationMessage("Bundle name", 4));
                  }

                  return Promise.resolve();
                },
              },
              {
                max: 255,
                message: VALIDATION.getMaxCharacterValidationMessage("Bundle name", 256)
              },
            ]}
          >
            <Input
              style={{
                height: "44px"
              }}
              placeholder="Bundle name"
              value={selectedBundle.description}
              onChange={(e) => {
                setSelectedBundle({
                  ...selectedBundle,
                  description: e.target.value
                });
              }}
            />
          </Form.Item>

          <Flex
            style={{
              width: "100%"
            }}
            gap={8}>
            <Switch
              onChange={(value) => {
                setIsCustomBundle(value);
                setSelectedBundle({
                  ...selectedBundle,
                  amount: !value ? AMOUNT_PER_CREDIT.mul(selectedBundle.credit).toNumber() : selectedBundle.amount
                });

                formRef.current?.setFieldsValue({
                  ...selectedBundle
                });
              }}
              checked={isCustomBundle}
              style={{
                marginTop: "3px",
                backgroundColor: isCustomBundle ? COLOR_PRIMARY_ORANGE : ""
              }}
            />

            <Flex vertical style={{ width: "100%" }}>
              <Typography.Text className={classes.customBundleInfoTitle}>
                Customize Bundle
              </Typography.Text>
              <Typography.Paragraph className={classes.customBundleInfoText}>
                Enable manual adjustment of credits and amount
              </Typography.Paragraph>
            </Flex>
          </Flex>

          <Form.Item
            normalize={(value) => (value?.toString() || "").replace(/[^0-9]/g, "")}
            label="Credits"
            name={"credit"}
            initialValue={selectedBundle.credit}
            className={classes.inputBox}
            rules={[
              {
                validator: (_, value: number) => {
                  if (value <= 0) {
                    return Promise.reject(VALIDATION.getMinValidationMessage(0));
                  }

                  if (value > 10000) {
                    return Promise.reject(VALIDATION.getMaxValidationMessage(10000));
                  }

                  return Promise.resolve();
                }
              }
            ]}>
            <Input
              style={{
                height: "44px"
              }}
              placeholder="Credits"
              value={selectedBundle.credit}
              onChange={(e) => {
                const value = Number(e.target.value);
                setSelectedBundle({
                  ...selectedBundle,
                  credit: value,
                  amount: !isCustomBundle ? AMOUNT_PER_CREDIT.mul(value).toNumber() : selectedBundle.amount
                });
              }}
            />
          </Form.Item>

          <Form.Item
            label="Amount in euros"
            name={"amount"}
            className={classes.inputBox}
            initialValue={selectedBundle.amount}
            rules={[
              {
                validator: (_, value: number) => {
                  if (value <= 0) {
                    return Promise.reject(VALIDATION.getMinValidationMessage(0));
                  }

                  if (value > 10000) {
                    return Promise.reject(VALIDATION.getMaxValidationMessage(10000));
                  }

                  return Promise.resolve();
                }
              }
            ]}>
            <InputNumber
              disabled={!isCustomBundle}
              style={{
                height: "44px",
                padding: 0,
                width: "100%",
                display: "flex",
                alignItems: "center"
              }}
              onKeyDown={(e) => {
                if (e.key === "e") {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Price"
              type="number"
              precision={2}
              controls={false}
              defaultValue={selectedBundle.amount}
              value={selectedBundle.amount}
              onChange={(value) => {
                setSelectedBundle({
                  ...selectedBundle,
                  amount: Number(value)
                });
              }}
            />

            {!isCustomBundle && (
              <Typography.Text className={classes.customBundleInfoText}>
                Calculated based on 1 credit = {AMOUNT_PER_CREDIT.toNumber()} euros. Adjust manually if 'Customize Bundle' is
                enabled
              </Typography.Text>
            )}
          </Form.Item>

          <Button
            loading={loading}
            onClick={async () => {
              try {
                await formRef.current?.validateFields();
              } catch {
                return;
              }

              if (
                isDuplicateBundle(selectedBundle.id, selectedBundle.credit, selectedBundle.amount)
              ) {
                message.error(VALIDATION.duplicateBundle);
                return;
              }

              setLoading(true);
              if (creatingBundle) {
                await saveBundle();
              } else {
                await editBundle();
              }
              await fetchBundles();
              setLoading(false);

              onCancel();
            }}
            style={{
              height: "44px",
              width: "100%",
              borderRadius: "8px",
              border: "none",
              fontFamily: "Titillium web",
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: "24px"
            }}>
            {creatingBundle ? "Create bundle" : "Save"}
          </Button>
        </Form>
      </Modal>
    </ConfigProvider>
  );
};
